<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ElMessage, ElPopover, ElButton as TcButton, ElDialog as TcConfirm, ElInput as TcField } from 'element-plus'
import { TcCheckBox } from '@overseas/tcdesign'
import {
  promoCodeRedeemClickEv,
  promoCodeUseClickEv,
  promoCodesPageLoadEv,
} from './coupon.track'
import classNames from '@/utils/classNames'
import Empty from '@/components/empty/index.vue'
import { request } from '@/utils/http'
import { trackSmFingerprint } from '~/composables/useSMFingerprint'

function showToast(err: Record<string, any>) {
  ElMessage({
    type: 'warning',
    customClass: 'coupon-toast',
    ...(err || {}),
    // @ts-ingore
    plain: true,
  })
}

function seaOctopusApiCouponCouponExchangePost(requestForm: Record<string, any>) {
  return request({
    url: '/sea_shell/sea_octopus_api/coupon/couponExchangeV2',
    method: 'POST',
    data: requestForm,
  })
}

function seaOctopusApiCouponMyCouponListPost(requestForm: Record<string, any>) {
  return request({
    method: 'POST',
    url: '/sea_shell/sea_octopus_api/coupon/myCouponListV2',
    data: requestForm,
  })
}

function seaOctopusApiGetProjectFilterListPost() {
  return request({
    method: 'POST',
    url: '/sea_shell/sea_octopus_api/coupon/getProjectFilter',
    data: {},
  })
}

interface SeaOctopusApiCouponMyCouponListPostData {
  /** 优惠信息 */
  couponInfo?: string
  /** 不能使用提示 */
  unUseTip?: string
  /** 优惠券码 */
  couponCode?: string
  /** 优惠券名称 */
  couponName?: string
  /** 优惠券有效期 */
  couponValidTime?: string
  /** 优惠券简单规则 */
  couponSimpleRule?: string
  /** 优惠券使用规则 */
  couponUseRule?: string
  /** 优惠券使用链接 */
  couponUseLink?: string
  /** 订单详情链接 */
  orderDetailLink?: string
  /** 订单Id */
  orderId?: string
  /** icon地址 */
  icon?: string
  /** 优惠券代码 */
  promotionCode?: string
  /** 标签列表 */
  tagList?: { tagContext: string }[]
  buttonText?: string
}

const { t, locale } = useI18n({
  inheritLocale: true,
})

interface TabItem {
  optType: number
  title: string
}

const prefixClass = 'coupon'
const prefixModalSuccessClass = 'success-exchange-modal'

// 获取路由参数
const route = useRoute()
const query = route.query

/** 输入框 */
const searchValue = ref('')
/** 输入框 */
const currentOptType = ref<number>(0)
/** tab list */
const tabList = ref<TabItem[]>([
  {
    optType: 0,
    title: t('coupon.p_coupon_available'),
  },
  {
    optType: 1,
    title: t('coupon.p_coupon_used'),
  },
  {
    optType: 2,
    title: t('coupon.p_coupon_not_available'),
  },
])

// 所有优惠券是否为空
const isAllCouponEmpty = ref(true)

// 网络问题
const isNetError = ref(false)

type CouponBodyMsgList = SeaOctopusApiCouponMyCouponListPostData[]

// 优惠券列表
const couponListData = ref<CouponBodyMsgList>([])

// 优惠券详情信息
const refundModal = ref(false)
// 兑换查询中
const exchangeLoading = ref(false)
// 成功提示
const showSuccessExchangeModal = ref(false)
// 详情页面
const detailCouponData = ref<CouponBodyMsgList[0]>()
// 兑换成功数据
const exchangeCouponData = ref<CouponBodyMsgList>([{}, {}])

const isFetchData = ref<boolean>(false)

const showLoading = ref<boolean>(false)

/** 我知道了是否禁用 */
const knowDisabled = ref<boolean>(false)
/** 记录是否完成动画 */
const scrollIteming = ref<boolean>(false)
// 是否登录
const isLogin = useComCookie('sectoken').value
// 是否聚焦
const isFocus = ref<boolean>(false)
// 可以使用数量
const canUseSize = ref<number>(0)

const timeSort = ref(1) // 默认最近新增

console.log('isFocus', isFocus.value)

const timeFilter = ref(false)
const productFilter = ref(false)
const filterBoxRef = ref<HTMLElement | null>(null)
const filterBoxRefProduct = ref<HTMLElement | null>(null)
// 产品类型枚举
const productTypeEnum = ref([])
const deepCloneProductTypeEnum = ref([])
// 快捷筛选项配置
const quickFilterConfig = ref<any>([])

const filterCount = computed(() => {
  return productTypeEnum.value.filter((item: any) => item.selected).length
})
// 筛选已选择的productTypeEnum的label集合
const currentProject = computed(() => {
  return productTypeEnum.value
    .filter((item: any) => item.selected)
    .map((item: any) => item.value)
})

watch(
  () => productTypeEnum.value,
  (newVal: any) => {
    const newQuickFilterConfig = quickFilterConfig.value.map((quickItem: any) => {
      const productItem = newVal.find((item: any) => item.value === quickItem.value)
      return {
        ...quickItem,
        selected: productItem ? productItem.selected : quickItem.selected,
      }
    })
    quickFilterConfig.value = newQuickFilterConfig
  },
  {
    deep: true,
  },
)

/** 获取deepCloneProductTypeEnum中selected的个数 */
const filterCountDeep = computed(() => {
  return deepCloneProductTypeEnum.value.filter((item: any) => item.selected).length
})

function onCancel() {
  deepCloneProductTypeEnum.value.forEach((item: any) => {
    item.selected = false
  })
}

function onConfirm() {
  productFilter.value = false
  productTypeEnum.value = deepCloneProductTypeEnum.value
  queryCouponList()
}

function showTimeFilter() {
  timeFilter.value = !timeFilter.value
}

function showProductFilter() {
  productFilter.value = !productFilter.value
  deepCloneProductTypeEnum.value = JSON.parse(
    JSON.stringify(productTypeEnum.value),
  )
}

/** 获取项目筛选配置 */
async function getQuickFilterConfig() {
  const res = await seaOctopusApiGetProjectFilterListPost()
  const { projectList = [], filterList = [] } = res || {}
  // 将projectList 转为productTypeEnum的格式
  productTypeEnum.value = filterList.map((item: any) => ({
    label: t(`coupon.p_coupon_${item}`),
    value: item,
    selected: false,
  }))
  quickFilterConfig.value = projectList.map((item: any) => ({
    label: t(`coupon.p_coupon_${item}`),
    value: item,
    selected: false,
  }))
}

function quickFilter(quickItem: any) {
  quickItem.selected = !quickItem.selected
  productTypeEnum.value = productTypeEnum.value.map((item: any) => {
    if (item.value === quickItem.value) {
      return {
        ...item,
        selected: quickItem.selected,
      }
    }
    return item
  })
  queryCouponList()
}

/** 点击叉号不失焦 */
function onMousedown(event: MouseEvent) {
  event.preventDefault()
  setTimeout(() => {
    isFocus.value = true
  }, 0)
  return true
}

function hanldeClear() {
  console.log('清空')
  searchValue.value = ''
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 容器引用
const listContainer = ref()
async function scrollToItem(itemId: string) {
  const elementLine = listContainer.value.querySelector(`#item-line-${itemId}`)
  const element = listContainer.value.querySelector(`#item-${itemId}`)
  scrollIteming.value = true
  if (elementLine) {
    const rect = element.getBoundingClientRect()
    const offsetY = rect.height + rect.y
    if (
      (rect.y > 0 && rect.y < rect.height)
      || rect.y < 0
      || offsetY > window.innerHeight - 2
    ) {
      elementLine.scrollIntoView({ behavior: 'smooth' })
    }
    await delay(500)
    element.classList.add('animation-ease')
    await delay(1200)
    element.classList.remove('animation-ease')
    scrollIteming.value = false
  }
}

/** 兑换 */
function handleExchange() {
  if (!isLogin) {
    return handleLogin()
  }
  const postData = {
    promotionCode: searchValue?.value || '',
    refId: (query?.refId as string) || '1910461249',
    channelId: '20002',
  }
  exchangeLoading.value = true

  seaOctopusApiCouponCouponExchangePost(postData)
    .then((res) => {
      exchangeCouponData.value = res || []
      showSuccessExchangeModal.value = true
      currentOptType.value = 0
      searchValue.value = ''
      try {
        promoCodeRedeemClickEv({
          promoCode: res?.map((item: any) => item.batchNo),
        })
      }
      catch (error) {
        console.error('res======', error)
      }
      nextTick(() => {
        knowDisabled.value = true
        initData(true)
      })
    })
    .catch((err) => {
      showToast({
        ...err,
        duration: 5000,
      })
      // 兑换成功但不可用
      if (err.status === 80302 || err.status === 30115) {
        currentOptType.value = 2
        searchValue.value = ''
        nextTick(() => {
          knowDisabled.value = true
          initData()
        })
      }
    })
    .finally(() => {
      exchangeLoading.value = false
    })
}

function reload() {
  initData()
}

function handleLogin() {
  location.href = `/${locale.value}/passport/signin?pageUrl=${encodeURIComponent(location.href)}`
}

function handleIknow() {
  showSuccessExchangeModal.value = false
  if (!knowDisabled.value) {
    nextTick(() => {
      if (exchangeCouponData?.value) {
        scrollToItem(exchangeCouponData?.value[0]?.couponCode || '')
      }
    })
  }
  else {
    showLoading.value = true
  }
}

/** 立即使用 */
function handleSuccessUse() {
  if (exchangeCouponData?.value) {
    handleUse(exchangeCouponData?.value[0])
  }
}

/** 使用优惠券 */
function handleUse(item: CouponBodyMsgList[0]) {
  try {
    promoCodeUseClickEv({
      promoCode: item?.batchNo || '',
    })
  }
  catch (error) {
    console.error('error=====', error)
  }
  if (!item?.couponUseLink) { return console.warn('优惠券使用链接为空') }
  location.href = item?.couponUseLink
}

/** 查看详情 */
function handleDetail(item: CouponBodyMsgList[0]) {
  detailCouponData.value = item
  refundModal.value = true
}

/** 复制优惠券code */
function handelCopy(code?: string) {
  const input = document.createElement('input')
  input.value = code || ''
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
  ElMessage({
    type: 'success',
    message: t('coupon.p_coupon_copy_success'),
  })
}

/** 切换tab */
function handleTabClick(optType: number) {
  currentOptType.value = optType
  queryCouponList()
}

/**
 * @description: 加载数据
 */
async function initData(hideLoading?: boolean) {
  const res = await seaOctopusApiCouponMyCouponListPost({ optType: 3, channelId: '20002' }).catch((err) => {
    showToast(err)
  })
  isAllCouponEmpty.value = !(res ? (res.couponList && res.couponList.length) : false)
  queryCouponList(hideLoading)?.then(() => {
    promoCodesPageLoadEv({
      havecode: couponListData.value.length ? 'yes' : 'no',
    })
  })
    .catch(() => {
      promoCodesPageLoadEv({
        havecode: 'no',
      })
    })
}

/** 查询优惠券列表 */
function queryCouponList(hideLoading?: boolean) {
  const postData = currentOptType.value === 0
    ? {
        optType: currentOptType.value,
        channelId: '20002',
        projectList: currentProject.value,
        timeSort: timeSort.value,
      }
    : {
        optType: currentOptType.value,
        channelId: '20002',
      }
  isFetchData.value = false
  if (!hideLoading) {
    showLoading.value = true
  }
  return seaOctopusApiCouponMyCouponListPost(postData)
    .then((res) => {
      isNetError.value = false
      const resultRes = (res && res.couponList) || []
      if (currentOptType.value === 0) {
        canUseSize.value = resultRes?.length
      }
      couponListData.value = resultRes
    })
    .catch((err) => {
      isNetError.value = true
      showToast(err)
    })
    .finally(() => {
      knowDisabled.value = false
      showLoading.value = false
      if (
        !scrollIteming.value
        && hideLoading
        && !showSuccessExchangeModal.value
      ) {
        nextTick(() => {
          scrollToItem(exchangeCouponData.value[0]?.couponCode || '')
        })
      }
      isFetchData.value = true
    })
}

function switchTimeSort(sort: number) {
  timeSort.value = sort
  queryCouponList()
}

function handleClickOutside(event: MouseEvent) {
  if (filterBoxRef.value && timeFilter.value) {
    const isClickInside = filterBoxRef.value?.contains(event.target as Node)
    if (!isClickInside) {
      timeFilter.value = false
    }
  }
  if (filterBoxRefProduct.value && productFilter.value) {
    const isClickInside = filterBoxRefProduct.value?.contains(event.target as Node)
    if (!isClickInside) {
      productFilter.value = false
    }
  }
}

function switchProductSelect(item: any) {
  item.selected = !item.selected
}

function stopPropagation(event: any) {
  event.stopPropagation()
}

onMounted(() => {
  setTimeout(() => {
    if (process.client) {
      document.addEventListener('click', handleClickOutside)
      if (!isLogin) { return }
      initData()
      getQuickFilterConfig()
    }
  })
  trackSmFingerprint({})
})
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="wrapper">
    <div v-loading="showLoading" :class="prefixClass">
      <span :class="`${prefixClass}-title`">{{ t('coupon.p_coupon_title') }}</span>
      <template v-if="isNetError && isLogin">
        <div class="net-error">
          <Empty
            img="NoNetwork"
            :title="t('coupon.p_coupon_no_network')"
            :subcontent="t('coupon.p_coupon_no_network_tips')"
          />
          <!-- 无网络重试按钮 -->
          <div class="mt28px px-16px">
            <TcButton class="brand retry-btn" block color="#00C868" @click="reload">
              {{
                t('coupon.p_coupon_retry')
              }}
            </TcButton>
          </div>
        </div>
      </template>
      <template v-else>
        <div :class="`${prefixClass}-header`">
          <div
            :class="
              classNames(`${prefixClass}-search`, {
                'hide-tab': isAllCouponEmpty,
              })
            "
          >
            <TcField
              v-model="searchValue"
              class="coupon-field"
              :placeholder="t('coupon.p_coupon_input_code')"
              :show-suffix="true"
              type="serach"
              @focus="isFocus = true"
              @blur="isFocus = false"
            >
              <template #suffix>
                <div v-if="isFocus && searchValue" :class="`${prefixClass}-extra-close`" @mousedown="onMousedown" @click="hanldeClear">
                  <svg
                    class="clear"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="3429"
                    width="200"
                    height="200"
                  >
                    <path
                      d="M512 938.666667C276.352 938.666667 85.333333 747.648 85.333333 512S276.352 85.333333 512 85.333333s426.666667 191.018667 426.666667 426.666667-191.018667 426.666667-426.666667 426.666667z m0-486.997334L421.546667 361.173333A42.666667 42.666667 0 1 0 361.130667 421.546667L451.669333 512l-90.496 90.453333a42.666667 42.666667 0 1 0 60.373334 60.373334L512 572.330667l90.453333 90.496a42.666667 42.666667 0 0 0 60.373334-60.373334L572.330667 512l90.496-90.453333a42.666667 42.666667 0 0 0-60.373334-60.373334L512 451.669333z"
                      fill="#cccccc"
                      p-id="3430"
                    />
                  </svg>
                </div>
                <TcButton
                  class="suffix-icon brand"
                  :disabled="!searchValue"
                  size="small"
                  @click="handleExchange"
                >
                  <img
                    v-if="exchangeLoading"
                    src="https://file.reshg.com/hopegoo/mix-websit/images/coupon_loading.gif"
                  >
                  <span v-else>{{ t('coupon.p_coupon_exchange') }}</span>
                </TcButton>
              </template>
            </TcField>
          </div>
          <div v-if="!isAllCouponEmpty && isLogin" :class="`${prefixClass}-tab`">
            <el-tabs v-model="currentOptType" class="tabs" @tab-change="handleTabClick">
              <el-tab-pane v-for="(item, index) in tabList" :key="index" :label="item.title" :name="item.optType" />
            </el-tabs>
            <!-- <div
              v-for="tab in tabList"
              :key="tab.optType"
              :class="
                classNames(`${prefixClass}-tab-item`, {
                  active: currentOptType === tab.optType,
                })
              "
              @click="handleTabClick(tab.optType)"
            >
              <div
                :class="classNames(`${prefixClass}-tab-item-title`, {
                  acitive: currentOptType === tab.optType,
                })"
              >
                {{ tab.title }}{{ tab.optType === 0 ? `（${canUseSize}）` : '' }}
              </div>
            </div> -->
          </div>
          <!-- 过滤项 -->
          <div v-if="!isAllCouponEmpty && isLogin && currentOptType === 0 && productTypeEnum.length" class="filter-wrap no-scroll-bar">
            <!-- 日期过滤 -->
            <div ref="filterBoxRef" class="filter-wrap-box lastest-add" :class="{ active: timeFilter }" @click="showTimeFilter">
              <span
                class="ifont iconfont icon_cell_common_homepage_exchange_fill filter-icon"
              />
              <div>{{ timeSort === 1 ? t('coupon.p_coupon_timesort_1') : t('coupon.p_coupon_timesort_2') }}</div>
              <span class="ifont iconfont filter-arrow" :class="timeFilter ? 'arrow_action_common_list_up_line' : 'arrow_action_common_list_down_line'" />

              <!-- 时间过滤弹窗 -->
              <div v-show="timeFilter" class="filter-wrap-box-fix">
                <div class="filter-wrap-box-fix-item" :class="{ active: timeSort === 1 }" @click="switchTimeSort(1)">
                  {{ t('coupon.p_coupon_timesort_1') }}<span v-if="timeSort === 1" class="ifont iconfont icon_action_common_list_check_line_selected fix-icon" />
                </div>
                <div class="filter-wrap-box-fix-item" :class="{ active: timeSort === -1 }" @click="switchTimeSort(-1)">
                  {{ t('coupon.p_coupon_timesort_2') }}<span v-if="timeSort === -1" class="ifont iconfont icon_action_common_list_check_line_selected fix-icon" />
                </div>
              </div>
            </div>
            <!-- 筛选 -->
            <div
              ref="filterBoxRefProduct"
              class="filter-wrap-box"
              :class="{ active: productFilter || filterCount > 0 }" @click="showProductFilter"
            >
              <span
                class="ifont iconfont icon_filter_common_personal_filter filter-icon"
              />
              <div>
                {{ t('coupon.p_coupon_filter') }}
                <span v-if="filterCount > 0">({{ filterCount }})</span>
                <span class="ifont iconfont filter-arrow" :class="productFilter ? 'arrow_action_common_list_up_line' : 'arrow_action_common_list_down_line'" />
              </div>
              <!-- 产品过滤弹窗 -->
              <div v-show="productFilter" class="filter-wrap-box-fix" @click.stop>
                <div class="product-title">
                  {{ t('coupon.p_coupon_filter_title') }}
                </div>
                <div
                  v-for="item in deepCloneProductTypeEnum" :key="item.value"
                  class="product-item"
                  @click="switchProductSelect(item)"
                >
                  <div>{{ item.label }}</div>
                  <TcCheckBox v-model="item.selected" size="18px" @click="stopPropagation" />
                </div>
                <div class="product-btn">
                  <div class="product-btn-cancel" :class="{ active: filterCountDeep > 0 }" @click="onCancel">
                    {{ t('coupon.p_coupon_filter_clear') }}
                  </div>
                  <div class="product-btn-confirm" @click="onConfirm">
                    {{ t('coupon.p_coupon_filter_confirm') }}
                  </div>
                </div>
              </div>
            </div>
            <!-- bg动态配置项 -->
            <div
              v-for="item in quickFilterConfig"
              :key="item.value"
              class="filter-wrap-box"
              :class="{ active: item.selected }"
              @click="quickFilter(item)"
            >
              <div>{{ item.label }}</div>
            </div>
          </div>
        </div>

        <template v-if="isLogin">
          <div
            v-if="(isFetchData || knowDisabled)"
            ref="listContainer"
            :class="
              classNames(`${prefixClass}-list`, {
                'hide-tab': isAllCouponEmpty,
              })
            "
          >
            <template v-if="couponListData?.length">
              <div
                v-for="item in couponListData"
                :id="`item-${item.couponCode}`"
                :key="item.couponCode"
                :class="`${prefixClass}-list-item`"
              >
                <!-- 提醒角标 -->
                <template v-if="currentOptType === 0">
                  <div
                    v-for="(tag, tagIndex) in item.tagList"
                    :key="tagIndex"
                    v-html="tag.tagContext"
                  />
                </template>
                <div
                  :id="`item-line-${item.couponCode}`"
                  :class="`${prefixClass}-list-item-border`"
                />
                <div :class="`${prefixClass}-list-item-left`">
                  <div :class="`${prefixClass}-list-item-left-img`">
                    <img :src="item?.icon">
                  </div>
                </div>

                <div :class="`${prefixClass}-list-item-right`">
                  <div
                    :class="
                      classNames(`${prefixClass}-list-item-right-title no-translate`, {
                        disabled: currentOptType !== 0,
                      })
                    "
                  >
                    {{ item.couponInfo }}
                  </div>
                  <div
                    :class="
                      classNames(`${prefixClass}-list-item-right-subtitle`, {
                        disabled: currentOptType !== 0,
                      })
                    "
                  >
                    {{ item.couponName }}
                  </div>

                  <div
                    v-if="item.promotionCode"
                    :class="`${prefixClass}-list-item-right-tips`"
                  >
                    {{ t('coupon.p_coupon_code') }}{{ item?.promotionCode }}
                  </div>

                  <div
                    v-if="currentOptType === 0"
                    :class="`${prefixClass}-list-item-right-tips`"
                  >
                    {{ item.couponSimpleRule }}
                  </div>

                  <div
                    v-if="currentOptType === 0"
                    :class="`${prefixClass}-list-item-right-usetime`"
                  >
                    {{ item.couponValidTime }}
                  </div>
                  <ElPopover :append-to-body="false" popper-class="popover-wapper" placement="bottom" width="400" trigger="click">
                    <template #reference>
                      <div
                        :class="`${prefixClass}-list-item-right-usetime-detail`"
                        @click="handleDetail(item)"
                      >
                        {{ t('coupon.p_coupon_detail') }}
                      </div>
                    </template>
                    <template #default>
                      <div :class="`${prefixClass}-modal-content`">
                        <!-- <div :class="`${prefixClass}-modal-content-title`">
                          {{ detailCouponData?.couponInfo }}
                        </div> -->
                        <div :class="`${prefixClass}-modal-content-name`">
                          {{ detailCouponData?.couponName }}
                        </div>
                        <div v-if="detailCouponData?.promotionCode" :class="`${prefixClass}-modal-content-code`">
                          {{ t('coupon.p_coupon_code') }}{{ detailCouponData?.promotionCode }}<span
                            class="copy"
                            @click="handelCopy(detailCouponData?.promotionCode)"
                          >{{ t('coupon.p_coupon_copy_code') }}</span>
                        </div>
                        <div :class="`${prefixClass}-modal-content-time`">
                          {{ t('coupon.p_coupon_valid_time')
                          }}{{ detailCouponData?.couponValidTime }}
                        </div>
                        <div :class="`${prefixClass}-modal-content-rule`">
                          <div :class="`${prefixClass}-modal-content-rule-title`">
                            {{ t('coupon.p_coupon_use_rule') }}
                          </div>
                          <div
                            :class="`${prefixClass}-modal-content-rule-info`"
                            v-html="detailCouponData?.couponUseRule"
                          />
                        </div>
                      </div>
                    </template>
                  </ElPopover>
                  <div
                    v-if="currentOptType === 1"
                    :class="`${prefixClass}-list-item-right-order-no`"
                  >
                    {{ t('coupon.p_coupon_order_no') }}{{ item.orderId }}
                  </div>
                  <div
                    v-if="currentOptType === 2"
                    :class="`${prefixClass}-list-item-right-unuse-tip`"
                  >
                    {{ item.unUseTip }}
                  </div>
                </div>
                <div
                  :class="
                    classNames(`${prefixClass}-list-item-line`, {
                      disabled: currentOptType !== 0,
                    })
                  "
                />

                <div :class="`${prefixClass}-list-item-options`">
                  <TcButton
                    :class="`${prefixClass}-list-item-options-btn ${(currentOptType === 0 || item.buttonText) ? 'brand' : ''} ${item.buttonText ? 'pending' : ''} br32`"
                    size="small"
                    :disabled="currentOptType !== 0"
                    @click="handleUse(item)"
                  >
                    {{ item.buttonText || t('coupon.p_coupon_use_now') }}
                  </TcButton>
                </div>
              </div>
              <div :class="`${prefixClass}-list-tips`">
                {{ t('coupon.p_coupon_list_tips') }}
              </div>
            </template>
            <template v-else>
              <div class="bg-#F3F3F3">
                <Empty
                  img="Order"
                  :title="t('coupon.p_coupon_iscoupon_empty')"
                />
              </div>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="no-login">
            <Empty
              img="Login"
              :title="t('coupon.p_coupon_login_tips')"
            />
            <!-- 无网络重试按钮 -->
            <div class="mt28px px-16px">
              <TcButton class="brand retry-btn" block color="#00C868" @click="handleLogin">
                {{ t('coupon.p_coupon_login_register') }}
              </TcButton>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
  <TcConfirm
    v-model="showSuccessExchangeModal"
    append-to-body
    :show-close="false"
    width="351px"
    :custom-class="prefixModalSuccessClass"
  >
    <div :class="`${prefixModalSuccessClass}-title`">
      {{ t('coupon.p_coupon_exchange_success_modal_title') }}
    </div>
    <div
      class="mt-[16px] pb-[16px] space-y-[8px] max-h-[276px] overflow-y-scroll no-scroll-bar"
    >
      <div
        v-for="item in exchangeCouponData"
        :key="item.couponCode"
        :class="`${prefixModalSuccessClass}-body`"
      >
        <div :class="`${prefixModalSuccessClass}-body-left`">
          <div :class="`${prefixModalSuccessClass}-body-left-img`">
            <img :src="item?.icon">
          </div>
        </div>
        <div :class="`${prefixModalSuccessClass}-body-line`" />
        <div :class="`${prefixModalSuccessClass}-body-right`">
          <div :class="`${prefixModalSuccessClass}-body-right-title no-translate` ">
            {{ item?.couponInfo }}
          </div>
          <div :class="`${prefixModalSuccessClass}-body-right-subtitle`">
            {{ item?.couponName }}
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div
        class="footer-wrap p-[16px] bg-white rounded-b-[12px] overflow-hidden w-full"
      >
        <TcButton
          :class="`${prefixModalSuccessClass}-use-now brand`"
          type="success"
          block
          size="default"
          @click="handleSuccessUse()"
        >
          {{ t('coupon.p_coupon_exchange_success_modal_nowuse') }}
        </TcButton>
        <div
          class="absolute -top-[14px] -right-[14px] w-[28px] h-[28px]  cursor-pointer"
          @click="handleIknow()"
        >
          <img
            alt=""
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFQAAABUCAMAAAArteDzAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAA5UExURUdwTPPz8/Pz8/Pz8+/v7/Pz8/Ly8u/v7/Ly8vPz8/Ly8vLy8vPz8/Ly8vHx8fPz8/Ly8vHx8fPz8/lfIVwAAAASdFJOUwDvz2QgX5AQz9+/Ra9wMKCngODeGNkAAAHDSURBVHja7dhLjsMgEEVRsPgVBlt5+19sZ9TVQqGrDIhR7iyTI4gBYZtv37Z3+VCI8I5SDv6aBmPNFk22eDdBnhmdyjlIesI/kR8ZJZOrWJeh6HZPzGqhyuoHGwPUBe3UDzwoOZVJeBS5tSarsrlcPTBQiv+aAW3za8BjsNo3ncVg1nXRG8PlhZPnzg5KmIgWDpTzwkDXDfUEt+xfLfiQDS9CE4WblAvA9XZ1TO2e7C3oqHtMtzGsstnZzlU3+2BYZfPdSzd/iw9RZPWvGQkfsq15dQ81VvnngY9dDVohq2wq13+AqLKpPVYzRFU0URo0QVTZ1O5UgqiyqUUBSZVNQIWyyuZ6NC1C2ZTVgQclqqRGeUmJamrQIppHEtX8dJsegKiGBvWyKau+QS/ZlNXr0SF96A9p+Zh6/ZqNGrqHlHxKh1+zUW/dHSV2bohsstq5xDujmz/dgdBEr2CF2S+89my6oM1fJfddes25cKBcxnD33lceUzGY3/0aaWLCQGnLq/m8Sm7z5w5W09yHmfk1EKLR5i1U2Woe5G4oym7RN0mOzl2fOuXOgk75NOM5XyyabK7RzHbVkBPhHVEJ/jLfvu3uB5x49rHVWbOSAAAAAElFTkSuQmCC"
          >
        </div>
      </div>
    </template>
  </TcConfirm>
</template>

<style lang="scss" scoped>
.wrapper {
  margin: 0 auto;
  width: 870px;
}

.coupon {
  position: relative;
  padding: 46px 0 80px 0px;
  min-height: 768px;
  font-size: 14px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);

  &-title {
    font-size: 36px;
    font-weight: 700;
    color: #000000;
  }

  :deep(.el-loading-mask) {
    background: transparent;
  }

  :deep(.el-button) {
    & > span {
      font-size: 16px!important;
      font-weight: bold;
    }
  }

  &-extra-close {
    font-size: 20px;
      margin-right: 8px;
      .clear {
        width: 20px;
        height: 20px;
        color: #ccc;
      }
    }

  .net-error, .no-login {
    text-align: center;
  }

  .retry-btn {
    width: 400px;
    height: 56px;
    border-radius: 9.33px;
    > span {
      font-style: normal !important;
      font-weight: 600 !important;;
      font-size: 18.67px !important;;
    }
  }

  &-header {
    width: 100%;
    background: white;
    // padding: 0 16px;

    // &::after {
    //   content: '';
    //   position: absolute;
    //   right: 0;
    //   bottom: 0;
    //   width: 100%;
    //   border-bottom: 1px solid #e4e4e4;
    // }
  }

  &-search {
    display: flex;
    margin-top: 24px;
    width: 400px;

    &.hide-tab {
      margin-bottom: 8px;
    }
  }

  &-tab {
    display: flex;
    margin-top: 40px;
    border-bottom: 1px solid #E4E4E4;

    .tabs {
      position: relative;
      -moz-user-select: none;
      -khtml-user-select: none;
      user-select: none;

      :deep(.el-tabs__item) {
        font-size: 20px;
        padding: 0 40px;
        font-weight: normal;
      }

      :deep(.el-tabs__item.is-top:nth-child(2)) {
        padding-left: 0;
      }

      :deep(.el-tabs__item.is-active) {
        font-size: 20px;
        color: var(--Brand-Green);
        font-weight: bold;
        cursor: default;
      }

      :deep(.el-tabs__item:hover) {
        color: var(--Brand-Green);
        cursor: pointer;
      }

      :deep(.el-tabs__nav-wrap::after) {
        display: none;
      }

      :deep(.el-tabs__active-bar) {
        height: 3px;
        background-color: var(--Brand-Green);
      }

      :deep(.el-tabs__header) {
        margin-bottom: 0;
      }
    }

    // &-item {
    //   flex: 1 1 33.33%;
    //   display: flex;
    //   flex-direction: column;
    //   align-items: left;
    //   position: relative;
    //   color: #000000;

    //   &-title {
    //     height: 38px;
    //     font-size: 20px;;
    //     display: flex;
    //     align-items: center;
    //     font-weight: 700;
    //     width: fit-content;

    //     &.acitive {
    //       border-bottom: 2px solid #00C868;
    //     }
    //   }

    //   &.active {
    //     color: #00C868;
    //     font-weight: bold;
    //   }
    // }
  }

  &-list {
    display: flex;
    flex-direction: column;
    position: relative;
    transition: top 0.5s cubic-bezier(0.33, 1, 0.68, 1);
    overflow-y: scroll;
    overscroll-behavior-y: none;

    &::-webkit-scrollbar {
      display: none;
    }

    // &.hide-tab {
    //   margin-top: 58px;
    // }

    &-item {
      background: #fff;
      border-radius: 8px;
      display: flex;
      margin-bottom: 16px;
      position: relative;
      border: 1px solid #e4e4e4;

      :deep(.coupon-list-item-warning) {
        position: absolute;
        top: -1px;
        right: -1px;
        padding: 1px 4px;
        border-radius: 0 4px;
        border: 0.5px solid #ffd5c3;
        background: #fff7f2;
        font-size: 12px;
        color: #ff4d00;
      }

      :deep(.coupon-list-item-success) {
        position: absolute;
        top: -1px;
        right: -1px;
        padding: 1px 4px;
        border-radius: 0 4px;
        border: 0.5px solid #ccebd9;
        background: #e8fff6;
        font-size: 12px;
        color: #00b55e;
      }

      &:first-child {
        margin-top: 17px;
      }

      &-border {
        width: 100%;
        height: 1px;
        background: #e4e4e4;
        position: absolute;
        top: -16px;
        left: 0;
        opacity: 0;
      }

      &-left {
        display: flex;
        align-items: center;
        position: relative;
        padding: 16px 40px;

        &-img {
          width: 32px;
          height: 32px;
        }
      }

      &-right {
        display: flex;
        flex-direction: column;
        position: relative;
        padding: 16px;
        width: 550px;
        padding-left: 0;

        &-title {
          color: #000000;
          font-size: 24px;
          font-weight: 700;
        }

        &-subtitle {
          margin-top: 2px;
          color: #000000;
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        &-tips {
          margin-top: 2px;
          font-weight: 400;
          color: #666666;
          font-size: 12px;
        }
        &-order-no{
          margin-top: 2px;
          font-weight: 400;
          color: #000000;
          font-size: 12px;
        }
        &-usetime,
        &-unuse-tip {
          margin-top: 8px;
          font-weight: 400;
          color: #000000;
          font-size: 12px;
        }
        &-unuse-tip{
          margin-top: 2px !important;
        }

        &-usetime {
          color: #666666;
          margin-top: 4px;
          font-weight: 400;
          font-size: 12px;
          display: flex;

          &-detail {
            color: #00C868;
            font-size: 12px;
            font-weight: normal;
            right: 44px;
            position: absolute;
            bottom: 17px;
          }
        }

        &-footer {
          margin-top: 16px;
          display: flex;
          align-items: center;

          &.mt8 {
            margin-top: 8px;
          }

          // &-detail {
          //   color: #00C868;
          //   font-size: 12px;
          //   font-weight: normal;
          // }

          &-btn {
            margin-left: auto;
            width: 88px;
            height: 28px;
            border-radius: 14px;
            font-size: 14px;
          }
        }
      }

      &-line {
        margin: 12px 0;
        position: relative;
        &::after {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          display: block;
          border-right: 1px dashed #e4e4e4;
        }

      }

      &-options {
        display: flex;
        width: 208px;
        align-items: center;
        position: relative;
        justify-content: center;

        &-btn {
          height: 36px;
          padding: 0 20px;
          &.is-disabled {
            opacity: 1;
          }
          &.pending {
            background: #90EEBA;
            border-color: #90EEBA;
            &:hover {
              color: #fff!important;
            }
          }
          span {
            font-size: 14px!important;
          }
        }

        &::before {
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
          top: -1px;
          content: '';
          display: block;
          width: 16px;
          height: 8px;
          // border-width: 0;
          border: 1px solid #E4E4E4;
          border-top-width: 0;
          background: white;
          position: absolute;
          left: 0;
          transform: translateX(-50%);
        }

        &::after {
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
          bottom: -1px;
          content: '';
          display: block;
          width: 16px;
          height: 8px;
          // border-width: 0;
          border: 1px solid #E4E4E4;
          border-bottom-width: 0;
          background: white;
          position: absolute;
          left: 0;
          transform: translateX(-50%);
        }
      }
    }

    .disabled {
      opacity: 0.4;
    }

    &-tips {
      color: #999999;
      font-size: 12px;
      margin-bottom: 8px;
    }
  }
}

.coupon-modal-content {
  display: flex;
  flex-direction: column;
  color: #000000;

  &-title {
    font-size: 24px;;
    font-weight: 700;
  }

  &-name {
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  }

  &-code {
    font-size: 14px;
    margin-top: 16px;

    .copy {
      color: #00C868;
      margin-left: 16px;
      position: relative;

      &::after {
        position: absolute;
        content: '';
        width: 1px;
        height: 10px;
        background: #e4e4e4;
        left: -8px;
        top: 15%;
        transform: translateX(-50%);
      }
    }
  }

  &-time {
    margin-top: 8px;
    font-size: 14px;
    font-weight: 400;
  }

  &-rule {
    margin-top: 24px;
    border-top: 0.5px dashed #e4e4e4;
    padding-top: 24px;

    &-title {
      font-size: 14px;
      font-weight: 600;
    }

    &-info {
      margin-top: 8px;
      font-size: 14px;
      white-space: pre-wrap;
    }
  }

  &-btn {
    height: 48px;
    margin-top: 16px;
    margin-bottom: 12px;
  }
}

.is-disabled {
  opacity: 0.4;

}

.suffix-icon {
  min-width: 120px;
  height: 100%;
  border-radius: 62px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: bold;

  &.is-disabled {
    // background-color: #99E3BB;
    color: white;
  }

  &.is-disabled:focus, &.is-disabled:hover {
    background-color: #009947;
    border-color: #009947;
  }

  img {
    width: 16px;
    height: 16px;
  }
}

.brand {
  background-color: #00C868;
  border-color: #00C868;
  color: white
}

.br32 {
  border-radius: 32px;
}
</style>

<style lang="scss">
.success-exchange-modal {
  top: 50%;
  position: absolute;
  left: 50%;
  margin: 0;
  transform: translate(-50%, -50%);
  padding-top: 24px;
  border-radius: 12px;
  text-align: center;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  background: linear-gradient(180deg, #00c868 25.55%, #fff 100%);

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0 16px ;
  }

  .el-dialog__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
  }

  &-title {
    font-size: 22px;
    font-weight: 700;
    color: #fff;
  }

  &-body {
    display: flex;
    // margin: 16px 0 24px;
    width: 100%;
    // background: #fff;
    background: url('./card-bg.png') 0px center / 100% 100% no-repeat;
    border-radius: 8px;
    overflow: hidden;

    &-left {
      display: flex;
      align-items: center;
      position: relative;
      padding: 10px 15px 10px 16px;

      &-img {
        width: 32px;
        height: 32px;
      }
    }

    &-line {
      width: 1px;
      // transform: scaleX(0.5) translateX(-4px);
      margin: 16px 0;
      background: #e4e4e4;
    }

    &-right {
      display: flex;
      flex-direction: column;
      position: relative;
      padding: 10px 16px;
      width: 100%;
      // &::before {
      //   border-bottom-left-radius: 16px;
      //   border-bottom-right-radius: 16px;
      //   top: -1px;
      //   content: '';
      //   display: block;
      //   width: 16px;
      //   height: 8px;
      //   border-width: 0;
      //   border: 1px solid #43b855;
      //   background: #43b855;
      //   position: absolute;
      //   left: 0;
      //   transform: translateX(-50%);
      // }

      // &::after {
      //   content: '';
      //   display: block;
      //   position: absolute;
      //   top: 0;
      //   right: 0;
      //   width: 40px;
      //   bottom: 0;
      //   pointer-events: none;
      //   background-color: #FFF;
      // }

      &-title {
        color: #000000;
        font-size: 22px;
        font-weight: 700;
        text-align: left;
      }

      &-subtitle {
        margin-top: 2px;
        color: #000000;
        font-size: 16px;
        font-weight: 600;
        text-align: left;
      }
    }
  }

  .success-exchange-modal-cancel-btn,
  .success-exchange-modal-use-now {
    border-radius: 60px !important;
    height: 56px;
    width: 100%;
    margin-left: 0 !important;
    > span {
      font-size: 18px;
      font-style: normal;
    }
  }

  .success-exchange-modal-use-now {
    > span {
      font-weight: bold
    }
  }

  .success-exchange-modal-cancel-btn {
    margin-top: 8px;
    border: 1.4px solid #00C868;
    background-color: #fff;
    color: #00C868;
    > span {
      font-weight: 400;
    }
  }

  .footer-wrap {
    box-shadow: 0 -2px 6px 0 #0000000d;
  }
}

/* 动画的关键帧 */
@keyframes fade-in-out {
  0% {
    background-color: white;
  }

  44% {
    background-color: rgba(0, 184, 85, 8%);
  }

  100% {
    background-color: white;
  }
}

.animation-ease {
  animation-name: fade-in-out;
  animation-duration: 565ms; /* 250ms + 125ms + 190ms = 565ms */
  animation-timing-function: ease-in-out;
  animation-iteration-count: 2;
}

.coupon-field {
  width: 400px;
  .el-input__wrapper {
    width: 400px;
    position: relative;
    padding: 4px;
    transition: all 0.3s;
    background-color: #fff;
    overflow: hidden;
    display: flex;
    align-items: center;
    border-radius: 40px;
  }

  .el-input__inner {
    width: 400px;
    overflow: hidden;
    border: none;
    outline: none;
    font-size: 14px;
    height: 40px;
    caret-color: #00C868;
    color: #000000;
    font-weight: bold;
    padding-left: 16px;
    &::placeholder {
      font-size: 14px;
      color: #999999;
      text-overflow: ellipsis;
      font-weight: normal;
    }
  }

  .el-input__suffix-inner {
    // flex-direction: row-reverse;
    > i {
      color: #CCCCCC
    }
    .el-input__clear {
    }
  }
}

.popover-wapper {
  overflow: visible !important;
  padding: 24px !important;
  width: 480px !important;
  border-radius: 8px !important;
  border: 1px solid #e5e7eb !important;
}

.coupon-toast {
  top: 98px !important;
  height: 48px !important;
  padding: 12px 16px  !important;
  gap: 8px !important;
  border-radius: 8px !important;
  background-color: #FFF !important;
  box-shadow: 0 0 8px 0 #0000001f !important;
  border: 1px solid transparent !important;
  > i {
    color: red !important;
    font-size: 20px !important;
  }
  .el-message__content {
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    color: #000000 !important;
    padding: 0px !important;
    line-height: 22.4px !important;
  }
}

.mt28px {
  margin-top: 28px;
}
.px-16px {
  padding: 0 16px;
}
.no-scroll-bar {
  &::-webkit-scrollbar {
    display: none;
  }
}

// 新增过滤项css
.filter-wrap {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  // overflow-x: scroll;
  margin-top: 24px;

  &-box {
    cursor: pointer;
    padding: 0 16px;
    height: 36px;
    border-radius: 4px;
    background: #f7f8fb;
    margin-right: 8px;
    font-size: 14px;
    color: #000000;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 0.5px solid transparent;
    flex-shrink: 0;
    position: relative;

    &-fix {
      min-width: 100%;
      position: absolute;
      bottom: 0;
      // 添加 transform 向下移动元素自身高度和额外的 16px
      transform: translate3d(0, calc(100% + 18px), 0);
      left: -1px;
      border-radius: 8px;
      border: 0.5px solid #E4E4E4;
      background: #FFF;
      box-shadow: 0 6px 20px 0 #686a6926;
      z-index: 2;
      padding: 16px 0;
      &-item {
        height: 36px;
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #000000;
        padding: 0 16px;
        &.active {
          color: #00c868;
        }
        .fix-icon {
          font-size: 16px;
          margin-left: 16px;
        }
        &:hover {
          background: #E6FFF3;
        }
      }

      .product-title {
        min-width: 260px;
        padding: 8px 16px;
        font-size: 16px;
        font-weight: bold;
        color: #000;
      }
      .product-item {
        padding: 0 16px;
        height: 28px;
        width: 100%;
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #000000;
        justify-content: space-between;
      }
      .product-btn {
        margin-top: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 16px;
        &-cancel {
          font-size: 12px;
          color: #90EEBA;
          cursor: pointer;
          &.active {
            color: #00c868;
          }
        }
        &-confirm {
          padding: 0 20px;
          height: 36px;
          background: #00c868;
          font-size: 14px;
          color: #fff;
          font-weight: bold;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 38px;
          cursor: pointer;
        }
      }
    }
    &.active {
      border: 0.5px solid #00c868;
      color: #00c868;
      background: #e6fff3;
    }

    &-logo {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }

    &.active {
      border: 0.5px solid #00c868;
      color: #00c868;
      background: #e6fff3;
    }
  }
}

.filter-content {
  padding: 0 16px;

  &-title {
    font-size: 16px;
    font-weight: bold;
    color: #000000;
    margin-bottom: 8px;
  }

  &-list {
    padding-bottom: 28px;

    &-item {
      width: 100%;
      height: 36px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
    }
  }
}

.filter-btn-wrap {
  display: flex;
  align-items: center;
  box-shadow: 0 -1px 10px 0 rgba(0, 0, 0, 10%);
  padding: 12px 16px 0;

  .sure-btn {
    height: 48px;
    margin-left: 8px;
    flex: 1;
    border-radius: 32px;
    font-weight: normal;
  }

  .cancel-btn {
    height: 48px;
    width: 128px;
    border-radius: 32px;
    background: #fff;
    border: 1px solid #00c868;
    color: #00c868;
    font-weight: normal;

    &::before {
      background: #fff !important;
    }

    &.active {
      &::before {
        background: #fff !important;
      }
    }
  }
}

.filter-icon {
  font-size: 16px;
  margin-right: 4px;
}
.filter-arrow {
  margin-left: 4px;
}
.tc-checkbox__child {
  &::after {
    border: 1px solid #000;
  }
}

.el-button > span {
  font-size: 14px!important;
}
</style>
